import { useLayoutEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAppState } from '../AppState/useAppState';
import { useModal } from '../Modal/useModal';
import { useElementTransformation } from '../MouseEvents/hooks/useElementTransformation';
import { useToast } from '../Toast/useToast';
import {
  handleKeyboardAction,
  KeyboardEffect,
  KeyboardHandlerContext,
} from './keyboardActionHandler';
import { mapKeyToAction, mapKeyUpToAction } from './keyboardActions';
import { computeWheelViewBox } from './wheelZoom';

const WHEEL_CANVAS_SELECTOR = '#container, [data-wheel-passthrough]';

const isTitleBlockInput = (node: EventTarget | null): boolean => {
  if (!(node instanceof HTMLInputElement)) return false;
  const element = node as HTMLInputElement;
  return element.closest('[data-title-block-input]') !== null;
};

const isTypingInTitleBlock = (target: EventTarget | null): boolean =>
  isTitleBlockInput(target) || isTitleBlockInput(document.activeElement);

export const useKeyboardEvents = () => {
  const hooks = useAppState();
  const {
    appState,
    session,
    selectedElements,
    setSelectedElements,
    selectionCoordinates,
    setSelectionCoordinates,
    selectionMode,
    setSelectionMode,
    viewBox,
    setViewBox,
    isShiftPressed,
    setIsSpacePressed,
    setIsShiftPressed,
    isMetaPressed,
    setIsMetaPressed,
    cursorPositionRef,
    commandManager,
    setShowAllSnaplines,
  } = hooks;
  const {
    onStopPolyline,
    onStartTextEditing,
    duplicateElement,
    pasteElements,
  } = useElementTransformation(hooks);
  const { addToast } = useToast();
  const intl = useIntl();

  const modal = useModal();

  const runEffect = (effect: KeyboardEffect) => {
    switch (effect.type) {
      case 'undo':
        commandManager.undo();
        return;
      case 'redo':
        commandManager.redo();
        return;
      case 'toggleSnaplines':
        setShowAllSnaplines((prev) => !prev);
        return;
      case 'executeCommand':
        commandManager.executeCommand(effect.command);
        return;
      case 'setSelectedElements':
        setSelectedElements(effect.ids);
        return;
      case 'setSelectionMode':
        setSelectionMode(effect.mode);
        return;
      case 'clearSelectionCoordinates':
        setSelectionCoordinates((prev) => ({
          ...prev,
          startX: null,
          startY: null,
          currentX: null,
          currentY: null,
        }));
        return;
      case 'setSpacePressed':
        setIsSpacePressed(effect.value);
        return;
      case 'setShiftPressed':
        setIsShiftPressed(effect.value);
        return;
      case 'setMetaPressed':
        setIsMetaPressed(effect.value);
        return;
      case 'nudgeViewBox':
        setViewBox((vB) => ({
          ...vB,
          x: vB.x + effect.dx,
          y: vB.y + effect.dy,
        }));
        return;
      case 'stopPolyline':
        onStopPolyline(effect.removeLastPoint);
        return;
      case 'startTextEditing':
        onStartTextEditing(effect.element);
        return;
      case 'blurActiveElement': {
        const activeElement = document.activeElement as HTMLElement | null;
        activeElement?.blur();
        return;
      }
      case 'duplicate':
        duplicateElement(
          cursorPositionRef.current.x,
          cursorPositionRef.current.y
        );
        return;
      case 'copyElements': {
        const payload = JSON.stringify({
          _ritabygglov: true,
          elements: effect.elements,
        });
        navigator.clipboard.writeText(payload).then(() => {
          addToast({
            type: 'success',
            message: intl.formatMessage(
              { id: 'canvas.copyToast' },
              { count: effect.elements.length }
            ),
          });
        });
        return;
      }
    }
  };

  const handlerContext: KeyboardHandlerContext = {
    appState,
    selectedElements,
    selectionMode,
    selectionCoordinates,
    viewBox,
    currentUserId: session?.currentUserId,
    hasSession: Boolean(session),
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (modal.isOpen) return;
    if (isTypingInTitleBlock(e.target)) return;

    const action = mapKeyToAction(e, {
      isMetaPressed,
      isShiftPressed,
      selectionMode,
    });
    if (!action) return;

    const { preventDefault, effects } = handleKeyboardAction(
      action,
      handlerContext
    );

    if (preventDefault) e.preventDefault();
    effects.forEach(runEffect);
  };

  const onKeyUp = (e: KeyboardEvent) => {
    if (modal.isOpen) return;

    const action = mapKeyUpToAction(e);
    if (!action) return;

    const { effects } = handleKeyboardAction(action, handlerContext);
    effects.forEach(runEffect);
  };

  const onWheel = (e: WheelEvent) => {
    if (modal.isOpen) return;
    const target = e.target as Element | null;
    if (!target?.closest?.(WHEEL_CANVAS_SELECTOR)) return;
    if (e.cancelable) {
      e.preventDefault();
    }

    const newViewBox = computeWheelViewBox(
      {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        offsetX: e.clientX,
        offsetY: e.clientY,
      },
      viewBox,
      isMetaPressed
    );
    if (newViewBox) {
      setViewBox(newViewBox);
    }
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      setIsMetaPressed(false);
      setIsSpacePressed(false);
      setIsShiftPressed(false);
    }
  };

  const onWindowBlur = () => {
    setIsMetaPressed(false);
    setIsSpacePressed(false);
    setIsShiftPressed(false);
  };

  const onPaste = (e: ClipboardEvent) => {
    if (modal.isOpen) return;
    const active = document.activeElement;
    if (
      active instanceof HTMLInputElement ||
      active instanceof HTMLTextAreaElement ||
      (active as HTMLElement)?.isContentEditable
    )
      return;

    const text = e.clipboardData?.getData('text/plain');
    if (!text) return;

    let isCanvasData = false;
    try {
      const parsed = JSON.parse(text);
      isCanvasData = !!parsed?._ritabygglov;
    } catch {
      // not JSON — let default paste proceed
    }

    if (!isCanvasData) return;

    e.preventDefault();
    pasteElements(
      text,
      cursorPositionRef.current.x,
      cursorPositionRef.current.y
    );
  };

  useLayoutEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('blur', onWindowBlur);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('blur', onWindowBlur);
      window.removeEventListener('paste', onPaste);
    };
  });

  return;
};
